// JSON endpoints for the assistant, built on the shared typed `request()` core.
// The streaming chat endpoint is separate (chat-client.ts) because it needs raw
// ReadableStream access that `request()` (which buffers + JSON-parses) can't give.

import { request, API_BASE, ApiError } from '../api/client.ts';
import { getActiveOrgId } from '../api/org-store.ts';
import type {
  ConfirmResponse,
  ConversationSummary,
  HistoryMessage,
} from './types.ts';

/** Apply or reject a staged action batch. Executes atomically server-side. */
export function confirmBatch(batchId: string, approve: boolean): Promise<ConfirmResponse> {
  return request<ConfirmResponse>('/assistant/confirm', {
    method: 'POST',
    body: { batch_id: batchId, approve },
  });
}

export interface UndoResponse {
  ok: boolean;
  entity_type?: string;
  entity_id?: string;
}

/** Revert an auto-applied reversible-single action within its undo window (docs/05 move #3). */
export function undoAction(batchId: string): Promise<UndoResponse> {
  return request<UndoResponse>('/assistant/undo', {
    method: 'POST',
    body: { batch_id: batchId },
  });
}

export function listConversations(): Promise<ConversationSummary[]> {
  return request<{ items: ConversationSummary[] }>('/assistant/conversations').then(
    (r) => r.items ?? [],
  );
}

/**
 * Load a conversation's messages. The contract pins the route but not the exact
 * message shape; we read it leniently (role + text, plus media_ids if present)
 * and skip anything unrecognizable so a schema tweak degrades to "some history
 * missing" rather than a crash. Assumptions reported to the lead for diffing.
 */
/** Coerce a timestamp field to epoch ms — accepts a number (epoch ms) or an
 *  ISO-8601 string. Undefined when absent/unparseable (staleness then can't apply). */
function parseTs(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? undefined : t;
  }
  return undefined;
}

export async function loadHistory(conversationId: string): Promise<HistoryMessage[]> {
  const raw = await request<{ messages?: unknown[] }>(
    `/assistant/conversations/${encodeURIComponent(conversationId)}`,
  );
  const messages = Array.isArray(raw.messages) ? raw.messages : [];
  const out: HistoryMessage[] = [];
  for (const m of messages) {
    if (!m || typeof m !== 'object') continue;
    const rec = m as Record<string, unknown>;
    const role = rec.role;
    if (role !== 'user' && role !== 'assistant') continue;
    // Accept either `text` or `content` as the body field.
    const text =
      typeof rec.text === 'string'
        ? rec.text
        : typeof rec.content === 'string'
          ? rec.content
          : '';
    out.push({
      id: typeof rec.id === 'string' ? rec.id : crypto.randomUUID(),
      role,
      text,
      media_ids: Array.isArray(rec.media_ids)
        ? (rec.media_ids.filter((x) => typeof x === 'string') as string[])
        : undefined,
      // Whatever the server stamps: created_at, else updated_at, else timestamp.
      created_at: parseTs(rec.created_at) ?? parseTs(rec.updated_at) ?? parseTs(rec.timestamp),
    });
  }
  return out;
}

export interface TranscribeResult {
  text: string;
}

/** 503 with code 'transcribe_unavailable' when Whisper isn't deployed. */
export class TranscribeUnavailableError extends Error {
  constructor() {
    super('Transcription is unavailable');
    this.name = 'TranscribeUnavailableError';
  }
}

/**
 * Send recorded audio for server-side transcription (voice fallback path).
 * Multipart, so it bypasses `request()` (which sets a JSON content-type).
 */
export async function transcribeAudio(blob: Blob, signal?: AbortSignal): Promise<TranscribeResult> {
  const form = new FormData();
  form.append('audio', blob, 'recording.webm');
  const headers: Record<string, string> = {};
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;

  const res = await fetch(`${API_BASE}/assistant/transcribe`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
    signal,
  });

  if (res.status === 503) throw new TranscribeUnavailableError();
  const text = await res.text();
  const data = text ? (JSON.parse(text) as { text?: string; error?: { code?: string } }) : {};
  if (!res.ok) {
    if (data.error?.code === 'transcribe_unavailable') throw new TranscribeUnavailableError();
    throw new ApiError(res.status, data.error?.code ?? 'transcribe_failed', 'Transcription failed');
  }
  return { text: data.text ?? '' };
}
